import { useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import {
  Dashboard, Inventory2, GppGood, Payments,
  Storefront, Insights, Settings, CreditCard, Help, Logout, ChevronRight, Close
} from '@mui/icons-material'
import { buildAuthModalPath, performLogout, resolveAuthUser } from '../lib/utils'

interface ProfileMenuProps {
  isOpen: boolean
  onClose: () => void
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useLanguage()
  const authUser = useMemo(() => resolveAuthUser(), [isOpen])
  const displayName = authUser?.nickname || authUser?.username || authUser?.email || authUser?.phone || t('profile.guest')
  const contactLabel = authUser?.email || authUser?.phone || ''
  const planLabel = authUser?.plan_name || (authUser?.plan === 'free' || !authUser?.plan ? t('profile.freePlan') : authUser?.plan.toUpperCase())
  const avatarText = displayName ? displayName.trim().charAt(0).toUpperCase() : ''
  const avatarUrl = authUser?.avatar || ''

  // 按 Escape 键关闭
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // 打开时禁止滚动
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleNavigate = (path: string) => {
    navigate(path)
    onClose()
  }

  const handleAuthNavigate = (mode: 'login' | 'signup') => {
    navigate(buildAuthModalPath(location.pathname, location.search, mode))
    onClose()
  }

  const handleSignOut = async () => {
    await performLogout()
    handleAuthNavigate('login')
  }

  // 使用 Portal 渲染到 body
  if (typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <>
      {/* Backdrop - 全屏遮罩 */}
      <div
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-[200] transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer - 从屏幕右边滑出 */}
      <div
        className={`fixed top-0 right-0 h-full w-[320px] bg-white shadow-2xl z-[210] transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors z-10"
        >
          <Close fontSize="small" />
        </button>

        {/* Header */}
        <div className="p-6 pt-16 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-4">
            <div className="size-14 rounded-full border-2 border-primary/10 shadow-md bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-lg overflow-hidden">
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
              ) : avatarText ? (
                <span>{avatarText}</span>
              ) : (
                <span className="material-symbols-outlined text-lg">person</span>
              )}
            </div>
            <div className="flex flex-col">
              <span className="text-slate-900 font-bold text-lg leading-tight">{displayName}</span>
              {contactLabel ? <span className="text-slate-500 text-sm truncate">{contactLabel}</span> : null}
              <span className="bg-primary/10 text-primary text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider mt-1 w-fit">{planLabel}</span>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Core Navigation */}
          <div className="py-3">
            <h4 className="px-5 py-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('profile.coreNav')}</h4>
            <MenuLink icon={<Dashboard fontSize="small" />} label={t('profile.workspace')} onClick={() => handleNavigate('/workspace')} showArrow />
            <MenuLink icon={<Inventory2 fontSize="small" />} label={t('profile.assetMgmt')} onClick={() => handleNavigate('/workspace')} showArrow />
            <MenuLink icon={<GppGood fontSize="small" />} label={t('profile.copyright')} onClick={() => handleNavigate('/copyright')} showArrow />
            <MenuLink icon={<Payments fontSize="small" />} label={t('profile.pricing')} onClick={() => handleNavigate('/pricing')} showArrow />
            <MenuLink icon={<Storefront fontSize="small" />} label={t('profile.storeAuth')} onClick={() => handleNavigate('/stores/bind')} showArrow />
            <MenuLink
              icon={<Insights fontSize="small" />}
              label={t('profile.marketIntel')}
              badge={t('profile.beta')}
              onClick={() => handleNavigate('/workspace')}
              showArrow
            />
          </div>

          <div className="h-px bg-slate-100 mx-5 my-1"></div>

          {/* Account */}
          <div className="py-3">
            <h4 className="px-5 py-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('profile.account')}</h4>
            <MenuLink icon={<Settings fontSize="small" />} label={t('profile.settings')} onClick={() => handleNavigate('/settings')} />
            <MenuLink icon={<CreditCard fontSize="small" />} label={t('profile.billing')} onClick={() => handleNavigate('/settings/billing')} />
            <MenuLink icon={<Help fontSize="small" />} label={t('profile.help')} onClick={() => window.open('https://help.yijianmai.com', '_blank')} />
          </div>
        </div>

        {/* Sign Out - Fixed at bottom */}
        <div className="border-t border-slate-100 bg-white shrink-0">
          <button
            data-auth-target={buildAuthModalPath(location.pathname, location.search, 'login')}
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-5 py-4 text-rose-500 hover:bg-rose-50 transition-colors group text-left"
          >
            <Logout fontSize="small" />
            <span className="text-sm font-bold">{t('profile.signOut')}</span>
          </button>
        </div>
      </div>
    </>,
    document.body
  )
}

const MenuLink: React.FC<{
  icon: React.ReactNode
  label: string
  badge?: string
  showArrow?: boolean
  onClick?: () => void
}> = ({ icon, label, badge, showArrow, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 px-5 py-3 text-slate-700 hover:bg-slate-50 transition-colors group text-left"
  >
    <span className="text-slate-400 group-hover:text-primary transition-colors">{icon}</span>
    <span className="text-sm font-medium flex-1">{label}</span>
    {badge && <span className="bg-amber-100 text-amber-700 text-[9px] font-bold px-1.5 rounded uppercase mr-2">{badge}</span>}
    {showArrow && <ChevronRight className="text-slate-300 group-hover:text-primary group-hover:translate-x-0.5 transition-all" style={{ fontSize: 18 }} />}
  </button>
)
